import { flatten, get, isArray, isEmpty } from 'lodash-es';

const WRONG_FIELD_MESSAGES = {
  gift_card: 'Please enter a gift card code in this field.',
  promo: 'Please enter a promo code in this field.',
};

export const REDEEM_CODE_ONCE_MESSAGE =
  'Sorry, you can only use this redeem code once.';

const DEFAULT_COUPON_ERROR = "Sorry, that didn't work";

/** Normalize API coupon errors into a display string. */
export function formatCheckoutCouponError(error, codeKind) {
  let raw = error;
  if (isArray(raw)) {
    raw = flatten(raw)
      .filter(Boolean)
      .join(' ');
  }
  if (raw == null || raw === '') {
    return codeKind === 'promo'
      ? REDEEM_CODE_ONCE_MESSAGE
      : DEFAULT_COUPON_ERROR;
  }
  const message = String(raw).trim();
  if (!message) {
    return codeKind === 'promo'
      ? REDEEM_CODE_ONCE_MESSAGE
      : DEFAULT_COUPON_ERROR;
  }
  if (codeKind === 'promo') {
    if (message === WRONG_FIELD_MESSAGES.promo) {
      return message;
    }
    return REDEEM_CODE_ONCE_MESSAGE;
  }
  return message;
}

function discountFromSingleResult(result, calculatePercentageFn) {
  const pct = get(result, 'data.discount_percentage');
  if (pct) {
    return calculatePercentageFn(pct);
  }
  const amount = get(result, 'data.discount_amount');
  if (amount != null && amount !== '') {
    return Number(amount);
  }
  return 0;
}

/** API stacked validate returns { gift_card, promo, stacked }; single-code returns { isPromo, data, code }. */
export function couponValidateResultMatchesField(result, codeKind) {
  if (!codeKind || isEmpty(result)) {
    return true;
  }
  if (result.stacked || result.gift_card || result.promo) {
    if (codeKind === 'gift_card') {
      return !isEmpty(result.gift_card);
    }
    if (codeKind === 'promo') {
      return !isEmpty(result.promo);
    }
  }
  if (codeKind === 'gift_card') {
    return result.isPromo === false;
  }
  if (codeKind === 'promo') {
    return result.isPromo === true;
  }
  return true;
}

export function wrongFieldMessageForCodeKind(codeKind) {
  return WRONG_FIELD_MESSAGES[codeKind] || 'Sorry, that did not work';
}

export function getDiscountForCouponField(
  result,
  codeKind,
  calculatePercentageFn,
) {
  if (isEmpty(result) || !codeKind) {
    return 0;
  }

  if (result.stacked) {
    if (codeKind === 'gift_card') {
      return (
        Number(result.stacked.sb_applicable) ||
        Number(result.stacked.gift_card_value) ||
        Number(get(result, 'gift_card.data.discount_amount')) ||
        0
      );
    }
    return (
      Number(result.stacked.promo_discount) ||
      (result.promo
        ? discountFromSingleResult(result.promo, calculatePercentageFn)
        : 0)
    );
  }

  if (codeKind === 'gift_card' && result.gift_card) {
    return (
      Number(get(result, 'gift_card.data.discount_amount')) ||
      discountFromSingleResult(result.gift_card, calculatePercentageFn)
    );
  }

  if (codeKind === 'promo' && result.promo) {
    return discountFromSingleResult(result.promo, calculatePercentageFn);
  }

  return discountFromSingleResult(result, calculatePercentageFn);
}

export function getCodeFromCouponFieldResult(result, codeKind) {
  if (isEmpty(result)) {
    return null;
  }
  if (codeKind === 'gift_card') {
    return (
      get(result, 'gift_card.code') ||
      (result.isPromo === false ? result.code : null)
    );
  }
  if (codeKind === 'promo') {
    return (
      get(result, 'promo.code') ||
      (result.isPromo === true ? result.code : null)
    );
  }
  return result.code || null;
}

export function getCheckoutCouponsFromResults(
  giftCardCodeResult,
  promoCodeResult,
  calculatePercentageFn,
) {
  const giftCardCode = getCodeFromCouponFieldResult(
    giftCardCodeResult,
    'gift_card',
  );
  const promoCode = getCodeFromCouponFieldResult(promoCodeResult, 'promo');

  let giftCardDiscount = giftCardCode
    ? getDiscountForCouponField(
        giftCardCodeResult,
        'gift_card',
        calculatePercentageFn,
      )
    : 0;
  const promoDiscount = promoCode
    ? getDiscountForCouponField(promoCodeResult, 'promo', calculatePercentageFn)
    : 0;

  // Promo validate with a companion GC returns stacked pricing — refresh GC line too.
  if (
    !isEmpty(giftCardCodeResult) &&
    promoCodeResult &&
    promoCodeResult.stacked &&
    !isEmpty(promoCodeResult.gift_card)
  ) {
    giftCardDiscount = getDiscountForCouponField(
      promoCodeResult,
      'gift_card',
      calculatePercentageFn,
    );
  }

  return {
    giftCardCode,
    giftCardDiscount: Number(giftCardDiscount) || 0,
    promoCode,
    promoDiscount: Number(promoDiscount) || 0,
  };
}

export function buildCouponValidateRequest({
  coupon,
  codeKind,
  companionGiftCardCode,
  companionPromoCode,
  ...rest
}) {
  if (codeKind === 'gift_card') {
    return {
      gift_card_code: coupon,
      ...(companionPromoCode ? { code: companionPromoCode } : {}),
      ...rest,
    };
  }
  if (codeKind === 'promo') {
    return {
      code: coupon,
      ...(companionGiftCardCode
        ? { gift_card_code: companionGiftCardCode }
        : {}),
      ...rest,
    };
  }
  return {
    code: coupon,
    ...rest,
  };
}
